/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, DestroyRef, computed, inject, signal } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule, AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { NavigationStart, Router } from '@angular/router';
import { AuthService } from '../../service/auth.service';
import { LIST_TYPE } from '../../modules/auth/tutor-profile-setup/tutor-profile-setup.component';
import { McqQuestionsService } from '../../service/mcq-questions.service';
import { EditorModule } from 'primeng/editor';
import { appNoSpaceAtFirst } from '../../core/directive/no-space.directive';
export interface Option { _id: string; name: string; }

@Component({
  selector: 'notchmark-new-mcq-set',
  standalone: true,
  imports: [ReactiveFormsModule, Button, InputTextModule, TextareaModule, MultiSelectModule, EditorModule, appNoSpaceAtFirst],
  templateUrl: './new-mcq-set.component.html',
  styleUrl: './new-mcq-set.component.scss'
})
export class NewMcqSetComponent implements OnInit {
  private fb = inject(FormBuilder);
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private _authService = inject(AuthService);
  private mcqService = inject(McqQuestionsService);

  subjects = signal<Option[]>([]);
   isEditMode = signal(false);
  examSetId = signal<string | null>(null);
  isLoading = signal(false);

  fieldConfigs = signal([
    { key: 'title', label: 'MCQ Set / Test Title', type: 'text', placeholder: 'Enter test title', colClass: 'w_100' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Brief description of the test', colClass: 'w_100' },
    { key: 'subjectIds', label: 'Syllabus / Topics Covered', type: 'select-multi', placeholder: 'Choose subjects', colClass: 'w_100' },
    { key: 'price', label: 'Price (SAR)', type: 'number', placeholder: 'Enter Price', colClass: 'w_50' },
    { key: 'totalQuestions', label: 'Total Questions', type: 'number', placeholder: 'Enter Question No', colClass: 'w_50' },
    { key: 'durationMin', label: 'Duration (minutes)', type: 'number', placeholder: 'Enter Duration Time', colClass: 'w_50' },
    { key: 'passingPercent', label: 'Passing Criteria (%)', type: 'number', placeholder: 'Enter Passing Percent', colClass: 'w_50' },
    { key: 'timeAfter1stAttemptHours', label: 'Access time after 1st attempt (hours)', type: 'number', placeholder: 'Enter Time After 1st Attempt', colClass: 'w_50' },
    { key: 'timePerQuestionSec', label: 'Time Per Question (sec)', type: 'number', placeholder: 'Enter Time', colClass: 'w_50' },
    { key: 'reattemptLimit', label: 'Max Reattempts', type: 'number', placeholder: 'Enter Re Attempt', colClass: 'w_50' }
  ]);

  form = this.fb.group({
    title: ['', [Validators.required, Validators.minLength(3)]],
    description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(400)]],
    subjectIds: [([] as string[]), [Validators.required]],
    price: [0, [Validators.required, Validators.min(0)]],
    totalQuestions: ['', [Validators.required, Validators.min(1)]],
    durationMin: ['', [Validators.required, Validators.min(1)]],
     timePerQuestionSec: [{ value: '', disabled: true }, [Validators.required, Validators.min(1)]],
    passingPercent: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
    reattemptLimit: ['', [Validators.required, Validators.min(0)]],
    timeAfter1stAttemptHours: ['', [Validators.required, Validators.min(0)]],
    rules: ['', [Validators.maxLength(400)]],
    sampleQuestions: this.fb.array([])
  });

  submitted = signal(false);
  formValue = computed(() => this.form.getRawValue());

    get sampleQuestions(): FormArray {
    return this.form.get('sampleQuestions') as FormArray;
  }
  ngOnInit(): void {
    this._authService.getSettingsData(`type=${LIST_TYPE.SUBJECTS}&sectionSub=true`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any[]) => this.subjects.set(this.toOptions(res)));
      
        const dialogData = this.config.data;
    if (dialogData?.mode === 'edit' && dialogData?.examSetId) {
      this.isEditMode.set(true);
      this.examSetId.set(dialogData.examSetId);
      this.loadExamSetData(dialogData.examSetId);
    }
this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.ref.close();
        }
      });

      this.setupAutoCalculation();
  }

private setupAutoCalculation(): void {
  const totalQuestionsControl = this.form.get('totalQuestions');
  const durationMinControl = this.form.get('durationMin');
  const timePerQuestionControl = this.form.get('timePerQuestionSec');

  // Disable the timePerQuestionSec field
  timePerQuestionControl?.disable();

  // Listen to changes on both fields
  this.form.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      const totalQuestions = Number(totalQuestionsControl?.value) || 0;
      const durationMin = Number(durationMinControl?.value) || 0;

      if (totalQuestions > 0 && durationMin > 0) {
        const timePerQuestion = Math.floor((durationMin * 60) / totalQuestions);
        // Convert to string before setting
        timePerQuestionControl?.setValue(timePerQuestion.toString(), { emitEvent: false });
      } else {
        timePerQuestionControl?.setValue('', { emitEvent: false });
      }
    });
}


   private loadExamSetData(id: string): void {
    this.isLoading.set(true);
    this.mcqService.getSingleExamSet(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
           const subjectIds = data.subjects?.map((subject: any) => subject._id) || [];
          this.form.patchValue({
            title: data.title || '',
            description: data.description || '',
            subjectIds: subjectIds,
            price: data.price || 0,
            totalQuestions: data.totalQuestions || '',
            durationMin: data.durationMin || '',
            timePerQuestionSec: data.timePerQuestionSec || '',
            passingPercent: data.passingPercent || '',
            reattemptLimit: data.reattemptLimit || '',
            timeAfter1stAttemptHours: data.timeAfter1stAttemptHours || '',
             rules: data.rules || ''
          });
            if (data.sampleQuestions && data.sampleQuestions.length > 0) {
            data.sampleQuestions.forEach((sq: any) => {
              this.addSampleQuestion(sq.question, sq.answer);
            });
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading exam set:', err);
          this.isLoading.set(false);
        }
      });
  }
   private toOptions(arr: any[]): Option[] {
      return (arr ?? []).map(x => ({ _id: x._id ?? x.code ?? x.value, name: x.name ?? x.label ?? String(x) }));
    }
    createSampleQuestionGroup(question = '', answer = ''): FormGroup {
    return this.fb.group({
      question: [question],
      answer: [answer]
    });
  }

  addSampleQuestion(question = '', answer = ''): void {
    if (this.sampleQuestions.length < 2) {
      this.sampleQuestions.push(this.createSampleQuestionGroup(question, answer));
    }
  }

  removeSampleQuestion(index: number): void {
    this.sampleQuestions.removeAt(index);
  }

  canAddMoreSamples(): boolean {
    return this.sampleQuestions.length < 2;
  }

  isInvalid(key: string): boolean {
    const c = this.form.get(key) as AbstractControl | null;
    return !!c && (c.invalid && (c.touched || this.submitted()));
  }

  getError(key: string): string {
    const c = this.form.get(key);
    if (!c) return '';
    if (c.hasError('required')) return 'This field is required';
    if (c.hasError('minlength')) return 'Too short';
    if (c.hasError('maxLength')) return 'Maximum limit allowed 400 characters';
    if (c.hasError('min')) return 'Value is too low';
    if (c.hasError('max')) return 'Value is too high';
    return 'Invalid value';
  }

onSubmit(): void {
    this.submitted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const formData = this.formValue();
    const payload: any = { ...formData,
    price: Number(formData.price) || 0,
    totalQuestions: Number(formData.totalQuestions) || 0,
    durationMin: Number(formData.durationMin) || 0,
    timePerQuestionSec: Number(formData.timePerQuestionSec) || 0,
    passingPercent: Number(formData.passingPercent) || 0,
    reattemptLimit: Number(formData.reattemptLimit) || 0,
    timeAfter1stAttemptHours: Number(formData.timeAfter1stAttemptHours) || 0
     };
    const rulesText = payload.rules?.replace(/<[^>]*>/g, '').trim();
    if (!rulesText) {
      delete payload.rules;
    }
    if (payload.sampleQuestions && payload.sampleQuestions.length > 0) {
      const validSamples = payload.sampleQuestions.filter(
        (sq: any) => sq.question?.trim() || sq.answer?.trim()
      );
      if (validSamples.length === 0) {
        delete payload.sampleQuestions;
      } else {
        payload.sampleQuestions = validSamples;
      }
    } else {
      delete payload.sampleQuestions;
    }

    if (this.isEditMode() && this.examSetId()) {
      this.mcqService.updateTestSet(this.examSetId()!, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.ref.close(true);
          },
          error: (err) => {
            console.error('Error updating exam set:', err);
          }
        });
    } else {
      this.mcqService.addTestSet(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.ref.close(true);
             this.router.navigate(['/mcq-sets']);
          },
          error: (err) => {
            console.error('Error creating exam set:', err);
          }
        });
    }
  }

  closeDialog(): void {
    this.ref.close();
  }
}
